import React, {useState} from 'react'
import {css} from '@emotion/react'
import {
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Text,
    Box
} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {defineMessages, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {PDCShippingPDPDetailsPD, PDCStyleGuideTile} from '../../page-designer/assets'
import {PDCShippingPDPDetailsLayout, PDCStyleGuideCarousel} from '../../page-designer/layouts'
import {usePage} from '@salesforce/commerce-sdk-react'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import useSiteType from '../../hooks/use-site-type'

import {pageUrlBuilder} from '../../utils/url-utils'
import {
    STYLEGUIDE_MEN_PAGE_ID,
    STYLEGUIDE_WOMEN_PAGE_ID,
    CODE_MARCHE_MEN,
    CODE_MARCHE_WOMEN,
    CODE_FORM_MDB
} from '../../constants'

//utils
import {generateProductDetailsHTML} from '../../utils/product-utils'
import {getPageDesignerRegion} from '../../utils/utils'

//css
import {productDetailsStyles} from '../../theme/components/project/product-details'
import {productCareStyles} from '../../theme/components/project/product-care'
import ProductAGEC from './product-agec'
import {useAGECProduct} from '../../hooks/use-product-variant'
import {usePageParameters} from '@app/hooks/use-page-parameters'

/**
 * ProductDetailsDrawer
 *
 * A component that displays a drawer containing product-related information.
 * The content displayed within the drawer is determined by the `currentContent` prop,
 * allowing for dynamic rendering of various types of product information such as details,
 * care instructions, sustainability, and shipping information.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isOpen - Indicates whether the drawer is open.
 * @param {function} props.onClose - Callback function to close the drawer.
 * @param {string} props.currentContent - Specifies the type of content to display in the drawer.
 * @param {string} [props.productDetails] - Optional product details content to display.
 * @param {string} [props.productCare] - Optional care instructions content to display.
 *
 * @returns {JSX.Element} The rendered drawer component.
 *
 * @example
 * <ProductDetailsDrawer
 *   isOpen={isDrawerOpen}
 *   onClose={handleClose}
 *   currentContent="details"
 *   productDetails="Detailed product content information here"
 *   productCare="Care content instructions here"
 * />
 */
const ProductDetailsDrawer = ({
    isOpen,
    onClose,
    currentContent,
    productDetails,
    shortDescription,
    longDescription,
    productCare,
    product
}) => {
    const PAGEDESIGNER_TO_COMPONENT = {
        'commerce_assets.pdcShippingPDP': PDCShippingPDPDetailsPD,
        'commerce_assets.pdcStyleGuideTile': PDCStyleGuideTile,
        'commerce_layouts.pdcShippingPDPLayout': PDCShippingPDPDetailsLayout,
        'commerce_layouts.pdcStyleGuideCarousel': PDCStyleGuideCarousel
    }

    const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

    // Get style guide PD component
    const isAdultSwimwear =
        (product?.c_orliweb_stat03_code_marche === CODE_MARCHE_MEN ||
            product?.c_orliweb_stat03_code_marche === CODE_MARCHE_WOMEN) &&
        product?.c_orliweb_code_form === CODE_FORM_MDB

    // Determine the style guide page ID only for adult swimwear case
    const styleGuidePageId = isAdultSwimwear
        ? product?.c_orliweb_stat03_code_marche === CODE_MARCHE_MEN
            ? STYLEGUIDE_MEN_PAGE_ID
            : STYLEGUIDE_WOMEN_PAGE_ID
        : ''
    const {data: styleGuidePage} = usePage({parameters: {pageId: styleGuidePageId}})
    const styleGuideComponent = getPageDesignerRegion(styleGuidePage, 'main').components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcStyleGuideCarousel'
    )
    if (styleGuideComponent) {
        styleGuideComponent.isPDP = true
        styleGuideComponent.selectedModelId = product?.c_orliweb_product_model
        styleGuideComponent.data.largeNbSlidesToShow = 3
        styleGuideComponent.data.xlargeNbSlidesToShow = 3
        styleGuideComponent.data.xxlargeNbSlidesToShow = 3
    }

    // Get shipping PD component
    const {isOutlet} = useSiteType()
    const parameters = usePageParameters('pdp-data' + (isOutlet ? '-outlet' : ''), {includeCountry: true})
    const {data: shippingPDP} = usePage({parameters})
    const shippingPDPComponent = getPageDesignerRegion(shippingPDP, 'main')?.components?.find(
        (comp) => comp?.typeId === 'commerce_layouts.pdcShippingPDPLayout'
    )

    // Static definition of messages
    const messages = defineMessages({
        details: {
            id: 'product_view.pdp.drawer_details',
            defaultMessage: 'Details'
        },
        sustainability: {
            id: 'product_view.pdp.drawer_sustainability',
            defaultMessage: 'Traceability'
        },
        care: {
            id: 'product_view.pdp.drawer_care',
            defaultMessage: 'Care'
        },
        shipping: {
            id: 'product_view.pdp.drawer_shipping',
            defaultMessage: 'Shipping'
        },
        productDetails: {
            id: 'product_view.pdp.product_details',
            defaultMessage: 'Details'
        },
        productCare: {
            id: 'product_view.pdp.product_care',
            defaultMessage: 'Care'
        }
    })

    const currentMessage = messages[currentContent]
    const AGECVariant = useAGECProduct(product, isOpen)
    /**
     * A mapping of content types to their corresponding JSX elements.
     * This object determines what content is displayed in the drawer based on the `currentContent` prop.
     *
     * @type {Object.<string, JSX.Element>}
     *
     * @property {JSX.Element} details - Displays product details. If `productDetails` is provided, it shows that content; otherwise, it defaults to a formatted message.
     * @property {JSX.Element} sustainability - Displays a sustainability message using `FormattedMessage`.
     * @property {JSX.Element} care - Displays care instructions. If `productCare` is provided, it shows that content; otherwise, it defaults to a formatted message.
     * @property {JSX.Element} shipping - Displays shipping information. It uses `PageContext.Provider` to provide context for the `Component` that renders shipping details.
     */
    const contentMap = {
        details: (
            <Box
                pl={'30px'}
                css={extendedProductDetailsStyles}
                dangerouslySetInnerHTML={{
                    __html: generateProductDetailsHTML({
                        productDetails,
                        shortDescription,
                        longDescription
                    })
                }}
            ></Box>
        ),
        sustainability: AGECVariant && (
            <ProductAGEC
                isAgecProduct={!!AGECVariant}
                madeIn={AGECVariant?.c_orliweb_code_pays_made_in}
                origineMatiere={AGECVariant?.c_orliweb_code_pays_origine_matiere}
                transformation={AGECVariant?.c_orliweb_code_pays_transformation}
                pourcentageMatiereRecyclee={AGECVariant?.c_orliweb_pourcentage_matiere_recyclee}
                contientMicroplastique={AGECVariant?.c_orliweb_contient_microplastique}
                contientSubstanceDangereuse={AGECVariant?.c_orliweb_contient_substance_dangereuse}
                agecPdfLocation={AGECVariant?.c_AGECPDFLocation}
            />
        ),
        care: (
            <Text>
                {productCare ? (
                    <Box
                        pl={'30px'}
                        width={'570px'}
                        fontSize={'md'}
                        css={productCareStyles}
                        dangerouslySetInnerHTML={{__html: productCare}}
                    ></Box>
                ) : (
                    <FormattedMessage {...messages.productCare} />
                )}
            </Text>
        ),
        shipping: (
            <Text pl={'30px'}>
                <PageContext.Provider value={contextValue}>
                    {shippingPDPComponent ? <Component component={shippingPDPComponent} /> : null}
                </PageContext.Provider>
            </Text>
        )
    }
    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={'lg'}>
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader as={'h2'} ml={'30px'} mt={'30px'} borderBottom="none">
                    {currentMessage && <FormattedMessage {...currentMessage} />}
                </DrawerHeader>
                <DrawerBody pr={[0, 0, 0, 0, 0]}>
                    {contentMap[currentContent] || (
                        <Text>
                            <FormattedMessage
                                id="product_view.pdp.coming_soon"
                                defaultMessage="Coming soon..."
                            />
                        </Text>
                    )}

                    {currentContent === 'details' && styleGuideComponent && (
                        <Box ml={10}>
                            <PageContext.Provider value={contextValue}>
                                <Component component={styleGuideComponent} />
                            </PageContext.Provider>
                            <Link
                                to={pageUrlBuilder(styleGuidePageId)}
                                ml={20}
                                mt={24}
                                display={'inline-block'}
                            >
                                <FormattedMessage
                                    defaultMessage="View all styles"
                                    id="product_details_carousel.link.view_all_styles"
                                />
                            </Link>
                        </Box>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

const extendedProductDetailsStyles = css`
    ${productDetailsStyles};
    .shortDescription {
        width: 494px;
    }
`
ProductDetailsDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentContent: PropTypes.string.isRequired,
    productDetails: PropTypes.string,
    longDescription: PropTypes.string,
    shortDescription: PropTypes.string,
    productCare: PropTypes.string,
    product: PropTypes.object
}

export default ProductDetailsDrawer
